/**
 * 🧯 Fehler aus der App an den Server melden (Admin-Board → Reiter „Fehler").
 *
 * Ziel: Probleme sehen, bevor ein Kunde sie meldet — Login geht nicht, Sitzung wurde beendet,
 * Drucker druckt nicht, Erkennung liefert nichts, Skriptfehler. Gemeldet werden nur Art, Meldung
 * und ein paar technische Angaben (App-Stand, Plattform, Sprache) — keine Paket- oder Kundendaten.
 *
 * Stört die App nie: kein await nötig, Fehler beim Melden werden verschluckt, höchstens
 * 25 Meldungen pro App-Start und dieselbe Meldung nur einmal pro Minute.
 */
import { Capacitor } from '@capacitor/core';
import { SERVER_URL, TOKEN_KEYS } from '../config/serverConfig';

const MAX_PRO_START = 25;
let gesendet = 0;
const zuletzt = {};

function bundle() {
  try {
    const s = [...document.scripts].map((x) => x.src).find((x) => /\/static\/js\/main\./.test(x)) || '';
    return (s.match(/main\.([a-z0-9]+)\.js/) || [])[1] || '';
  } catch { return ''; }
}

export function reportError(kind, message, context) {
  try {
    if (!kind || gesendet >= MAX_PRO_START) return;
    const text = String(message === undefined || message === null ? '' : (message.message || message)).slice(0, 500);
    const schluessel = `${kind}|${text.slice(0, 100)}`;
    const jetzt = Date.now();
    if (jetzt - (zuletzt[schluessel] || 0) < 60000) return;
    zuletzt[schluessel] = jetzt;
    gesendet += 1;

    let token = '', konto = '';
    try { token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) || ''; } catch { /* */ }
    try { konto = (JSON.parse(localStorage.getItem('kiosk_user_data') || '{}') || {}).email || ''; } catch { /* */ }
    let rolle = '', sprache = '';
    try { rolle = localStorage.getItem('kiosk_device_role') || ''; sprache = localStorage.getItem('app_language') || ''; } catch { /* */ }

    fetch(`${SERVER_URL}/client-log`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        kind, message: text,
        platform: `${Capacitor.getPlatform()}${Capacitor.isNativePlatform() ? '-app' : ''}`,
        appVersion: bundle(),
        // kontoLautGeraet immer mitgeben: ein abgelaufenes Token (genau der Fall „abgemeldet") nennt dem Server kein Konto mehr
        context: { ...(context || {}), sprache, rolle, online: typeof navigator !== 'undefined' ? navigator.onLine : null,
          kontoLautGeraet: konto || null },
      }),
    }).catch(() => { /* Melden darf nie stören */ });
  } catch { /* */ }
}

export async function submitUserFeedback({ category = 'Feedback', message = '', contactReply = '' } = {}) {
  const text = String(message || '').trim().slice(0, 2000);
  if (!text) throw new Error('Bitte eine Nachricht eingeben.');

  let token = '', konto = '';
  try { token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) || ''; } catch { /* */ }
  try { konto = (JSON.parse(localStorage.getItem('kiosk_user_data') || '{}') || {}).email || ''; } catch { /* */ }
  let rolle = '', sprache = '', branche = '';
  try {
    rolle = localStorage.getItem('kiosk_device_role') || '';
    sprache = localStorage.getItem('app_language') || '';
    branche = localStorage.getItem('kiosk_branche') || '';
  } catch { /* */ }

  const res = await fetch(`${SERVER_URL}/client-log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      kind: 'user_feedback',
      message: text,
      platform: `${Capacitor.getPlatform()}${Capacitor.isNativePlatform() ? '-app' : ''}`,
      appVersion: bundle(),
      context: {
        category: String(category || 'Feedback').slice(0, 60),
        contactReply: String(contactReply || '').trim().slice(0, 150),
        sprache,
        rolle,
        branche,
        kontoLautGeraet: konto || null,
      },
    }),
  });
  if (!res.ok) throw new Error('Feedback konnte nicht gesendet werden.');
  return { ok: true };
}

// Für Stellen ohne Modul-Import (z. B. index.html) und zum Testen
try { if (typeof window !== 'undefined') window.__reportError = reportError; } catch { /* */ }

